import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./courseCreditsDashboard.scss";
import videoIcon from "../../../../assets/video-icon.svg";
import documentIcon from "../../../../assets/document-icon.svg";
import exitDoor from "../../../../assets/credits/exit_door.svg";
import challengesAdapter, { IChallenges, ItemType } from "adapters/challenges.adapter";
import {
  findCurrentChallenge,
  isSubChallengesCompleted,
} from "utils/challengesUtils";
import ChallengeCheck from "../../challenges-dashboard/challengeCheck";
import EvaluationCredits from "../evaluation-credits/EvaluationCredits";
import { CurrentChallengeInterface } from "../CourseCredits";
import authContext from "shared/src/contexts/authContext";
import { getToken } from 'shared/src/services/user-info.service';
import { validateIfcompleteCourse } from "services/routeServices";
import ConfirmModal from "../confirm-modal/ConfirmModal";

interface IChallengeDashboardProps {
  challenges: IChallenges[] | null;
  setChallenges: any;
  currentChallengeProps: CurrentChallengeInterface | undefined
}

interface IMedalModalProps {
  handleClose: any;
  children?: any;
  open: boolean;
}

const CourseCreditsDashboard = (
  { challenges, setChallenges, currentChallengeProps }: IChallengeDashboardProps,

  props: IMedalModalProps
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentChallenge, setCurrentChallenge] = useState<[number, number]>([
    0, 0,
  ]);
  const [openedChallenges, setOpenedChallenges] = useState<Array<number>>([0]);
  const [mobileOpenChallenges, setMobileOpenChallenges] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [responseModalValue, setResponseModalValue] = useState<boolean>();
  const [responseModalAction, setResponseModalAction] = useState<string>();
  const [showEvaluation, setShowEvaluation] = useState<boolean>(false);
  const [evaluationBlocked, setEvaluationBlocked] = useState<boolean>(false);
  const [evaluationComplete, setEvaluationComplete] = useState<boolean>(false);
  const [docId, setDocId] = useState('');
  const [typeDoc, setTypeDoc] = useState('');

  const auth: string|any = useContext(authContext);

  const calculatedCurrentChallenge = useMemo(() => {
    let challenge: any;
    if (challenges) {
      challenge = findCurrentChallenge(challenges);
    }
    return challenge;
  }, [challenges]);

  const currentSubChallenge = useMemo(() => {
    if (challenges) {
      const pos = challenges?.findIndex(item => item.idChallenge === (currentChallenge[0] || 0));
      return challenges[pos !== -1 ? pos : 0]?.subChallengeDTO?.[currentChallenge[1]] || 0;
    }
    return null;
  }, [currentChallenge, challenges]);

  const isCurrentChallengeCorrect = useMemo(() => {
    if (calculatedCurrentChallenge) {
      return (
        currentChallenge[0] === calculatedCurrentChallenge.challengeDTO.idChallenge &&
        currentChallenge[1] === calculatedCurrentChallenge.subChallengeIndex
      );
    }
  }, [calculatedCurrentChallenge, currentChallenge]);

  const isCurrentChallengeComplete = useMemo(() => {
    if (challenges) {
      const pos = challenges?.findIndex(item => item.idChallenge === (currentChallenge[0] || 0));
      return challenges[pos !== -1 ? pos : 0]?.subChallengeDTO?.[currentChallenge[1]].completed || 0;
    }

    return false;
  }, [challenges, currentChallenge]);

  const toggleChallenges = (challengeId: number) => {
    if (openedChallenges.includes(challengeId)) {
      setOpenedChallenges(
        openedChallenges.filter((challenge) => challenge !== challengeId)
      );
      return;
    }
    setOpenedChallenges([...openedChallenges, challengeId]);

  };

  const handleSelectCurrentChallenge = (
    challengeId: number,
    challengeIndex: number
  ) => {
    setShowEvaluation(false);
    setCurrentChallenge([challenges ? challenges[challenges.findIndex(item => item.idChallenge === challengeId)].idChallenge : 0, challengeIndex]);
    const params = {
      challengeId: challenges ? challenges[challenges.findIndex(item => item.idChallenge === challengeId)].idChallenge.toString() : "0",
      challengeIndex: challengeIndex.toString(),
    };

    const paramsResult = new URLSearchParams(params).toString();

    setSearchParams(paramsResult);
    setMobileOpenChallenges(false);

  };

  const handleNextChallenge = () => {

    if (!challenges) {
      return;
    }
    const challengeIdx = currentChallenge[0];
    const subChallengeIdx = currentChallenge[1];
    const positionChallenge = challenges.findIndex(item => item.idChallenge === challengeIdx)
    // If we are at the end of the sub-challenges, move to the next challenge at subchallenge index = 0
    if (
      subChallengeIdx ===
      challenges[positionChallenge]?.subChallengeDTO.length - 1
    ) {
      if (positionChallenge === challenges.length - 1) {
        setShowEvaluation(true);
        return;
      }
      handleSelectCurrentChallenge(challenges[positionChallenge + 1]?.idChallenge, 0);
      return;
    }

    handleSelectCurrentChallenge(challenges[positionChallenge]?.idChallenge, subChallengeIdx + 1);
  };

  const handlePrevChallenge = () => {
    if (!challenges) {
      return;
    }

    const challengeIdx = currentChallenge[0];
    const subChallengeIdx = currentChallenge[1];
    const positionChallenge = challenges.findIndex(item => item.idChallenge === challengeIdx)

    // If we are the start of the sub-challenges move the challenge to the previous index
    if (subChallengeIdx === 0) {
      if (positionChallenge === 0) {
        return;
      }

      handleSelectCurrentChallenge(
        challenges[positionChallenge - 1].idChallenge,
        challenges[positionChallenge - 1].subChallengeDTO.length - 1
      );
      return;
    }

    handleSelectCurrentChallenge(challengeIdx, subChallengeIdx == 0 ? 0 : subChallengeIdx - 1);
  };

  const handleChallengeDone = async (
    challengeIdx: number,
    subChallengeIdx: number
  ) => {
    if (!challenges) {
      return;
    }

    if (!calculatedCurrentChallenge || !isCurrentChallengeCorrect) {
      return;
    }

    if (challenges[challenges.findIndex(item => item.idChallenge === challengeIdx)]?.subChallengeDTO[subChallengeIdx].completed) {
      return;
    }

    const challengesDummy = JSON.parse(JSON.stringify(challenges));

    try {
      setChallenges(challengesDummy);
      // if (subChallengeIdx === 1) setShowModal(true);
      challengesDummy[challenges.findIndex(item => item.idChallenge === challengeIdx)]
        .subChallengeDTO[subChallengeIdx]
        .completed = true;

      localStorage.setItem('GET_CHALLENGES_USER', JSON.stringify(challengesDummy));

    } catch (error) {
      alert("Ocurrió un error al guardar el desafío");
    }
  };

  const isFinalChallenge = (challengeIdx: number, subChallengeIdx: number) => {
    if (!challenges) {
      return;
    }
    const isChildFinal =
      subChallengeIdx === challenges[challengeIdx]?.subChallengeDTO.length - 1;
    if (challengeIdx === challenges.length - 1 && isChildFinal) {
      return true;
    }
    return false;
  };

  const handleMobileToggleChallenges = () => {
    setMobileOpenChallenges(!mobileOpenChallenges);
  };

  const handlePreviousRoute = () => {
    if (currentChallenge[0] === 0 && currentChallenge[1] === 0) {
      navigate("/");
      return;
    }
    handlePrevChallenge();
  };

  const validateIfcompleteCourseApi = async (docId:string, typeDoc:string) => {
    try {
      const response:string|any = await validateIfcompleteCourse(docId, typeDoc);
      setEvaluationComplete(response)
    } catch (error) {
      console.error("Error al obtener las preguntas del curso.");
    }
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (headerRef.current && !headerRef.current?.contains(event.target)) {
        // setMobileOpenChallenges(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    // Limpia el oyente de eventos cuando el componente se desmonta.
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [headerRef]);

  useEffect(() => {
    const challengeId = searchParams.get("challengeId");
    const challengeIndex = searchParams.get("challengeIndex");

    if (challengeId && challengeIndex && challenges) {
      const challengeIndexNumber = parseInt(challengeIndex);
      const challengeIdNumber = parseInt(challengeId);
      setCurrentChallenge([challengeIdNumber, challengeIndexNumber]);
      setOpenedChallenges([challengeIdNumber]);
    }

  }, [challenges]);

  useEffect(() => {
    let isBlocked = false;
    challenges?.forEach(challenge => {
      challenge.subChallengeDTO.forEach(subChallenge => {
        if (!subChallenge.completed) {
          isBlocked = true;
          return;
        }
      });
      if (isBlocked) return;
    });

    setEvaluationBlocked(isBlocked);
  }, [challenges]);

  useEffect(() => {

    const GET_CHALLENGES_USER = localStorage.getItem('GET_CHALLENGES_USER');

    if (GET_CHALLENGES_USER != null) {
      setChallenges(challengesAdapter(JSON.parse(GET_CHALLENGES_USER)));
    }

    if (currentChallengeProps) {
      handleSelectCurrentChallenge(currentChallengeProps.challengeDTO.idChallenge, currentChallengeProps.subChallengeIndex);
    }

  }, [currentChallengeProps])

  useEffect(() => {
    if (responseModalValue == true) {
      if (responseModalAction === "changeAnElementOfAModule") {
        handleSelectCurrentChallenge(currentChallenge[0], currentChallenge[1]);

        setResponseModalValue(false);
        setResponseModalAction("");

        localStorage.setItem("evaluationInProgress", "false");
      }
      if (responseModalAction === "leaveThePage") {

        setResponseModalValue(false);
        setResponseModalAction("");

        localStorage.setItem("evaluationInProgress", "false");
        window.close();
      }
    }
  }, [responseModalValue]);

  useEffect(()=>{
    if(!auth)return;
    setDocId(auth.gigyaAccount?.data?.docNumber);
    setTypeDoc(auth.gigyaAccount?.data?.docType);
  },[auth])
  
  useEffect(() => {
    if(!auth && auth !== undefined) return;
    if(docId !== undefined && typeDoc !== undefined ){
      validateIfcompleteCourseApi(docId, typeDoc);
    }
    localStorage.setItem("evaluationInProgress", "false");
  }, [auth, docId, typeDoc]);

  if (!challenges) {
    return <></>;
  }

  return (
    <div className="challenge-dashboard">
      {/* Desktop challenges sidebar */}
      <aside className="challenge-dashboard-sidebar">
        <div>
          {challenges.map((challenge, idx) => {
            const isParentOpen = openedChallenges.includes(challenges[idx].idChallenge);
            const isOwnChildSelected = currentChallenge[0] === challenges[idx].idChallenge && currentChallenge[1] !== undefined;
            return (
              <div key={challenge.idChallenge}>
                <button
                  onClick={() => toggleChallenges(challenges[idx].idChallenge)}
                  className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-parent ${isOwnChildSelected
                    && !showEvaluation ? "challenge-dashboard-sidebar__button--active"
                    : ""
                    }`}
                >
                  <span>{`Módulo ${idx + 1}`}</span>
                  <span className="material-icons">
                    {isParentOpen && !showEvaluation ? "expand_less" : "expand_more"}
                  </span>
                </button>
                <div
                  className={`challenge-dashboard-sidebar__challenge ${isParentOpen
                    ? "challenge-dashboard-sidebar__challenge--open"
                    : ""
                    }`}
                >
                  {challenge.subChallengeDTO.map((child, childIdx) => {
                    const isChildActive = currentChallenge[0] === challenges[idx].idChallenge && currentChallenge[1] === childIdx;
                    return (
                      <button
                        key={child.title}
                        onClick={() => {
                          const evaluationInProgress = localStorage.getItem("evaluationInProgress");
                          setCurrentChallenge([challenges[idx].idChallenge, childIdx]);
                          if (evaluationInProgress && evaluationInProgress == "true") {
                            setShowModal(true);
                            return;
                          }
                          handleSelectCurrentChallenge(challenges[idx].idChallenge, childIdx)
                        }

                        }
                        className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-child ${isChildActive
                          && !showEvaluation ? "challenge-dashboard-sidebar__button-child--active"
                          : ""
                          }`}
                      >
                        <img
                          src={
                            child.type === ItemType.Text
                              ? documentIcon
                              : videoIcon
                          }
                          alt={child.title + " icon"}
                          width={32}
                          height={32}
                        />
                        <span>{child.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div key={99999}>
            <button
              onClick={() => { setShowEvaluation(true) }}
              className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-parent ${showEvaluation
                ? "challenge-dashboard-sidebar__button--active"
                : ""
                }`}
            >
              <span>Evaluación</span>
              <span className="material-icons">
                {showEvaluation ? "expand_less" : "expand_more"}
              </span>
            </button>
          </div>
        </div>
      </aside>
      <main id="dashboard-content" className="challenge-dashboard-content">
        {/* Mobile challenges bar */}
        <div className="challenge-dashboard-header">
          <div
            className="challenge-dashboard-header__container"
            ref={headerRef}
          >
            {
              showEvaluation ?
                <button
                  className="challenge-dashboard-header__display-item"
                  onClick={handleMobileToggleChallenges}
                >
                  <ChallengeCheck
                    checked={evaluationComplete}
                  />
                  <span>Evaluación </span>
                  <span className="material-icons">
                    {mobileOpenChallenges ? "expand_less" : "expand_more"}
                  </span>
                </button>
                :
                <button
                  className="challenge-dashboard-header__display-item"
                  onClick={handleMobileToggleChallenges}
                >
                  <ChallengeCheck
                    checked={isSubChallengesCompleted(
                      challenges,
                      currentChallenge[0]
                    )}
                  />
                  <span>Módulo {challenges.findIndex(item => item.idChallenge === currentChallenge[0]) + 1}</span>
                  <span className="material-icons">
                    {mobileOpenChallenges ? "expand_less" : "expand_more"}
                  </span>
                </button>
            }
            {mobileOpenChallenges && (
              <div className="challenge-dashboard-header__items-container">
                {challenges.map((challenge) => {
                  const isParentOpen = openedChallenges.includes(
                    challenge.index
                  );
                  const isOwnChildSelected =
                    currentChallenge[0] === challenge.index &&
                    currentChallenge[1] !== undefined;
                  return (
                    <div key={challenge.idChallenge}>
                      <button
                        className={`challenge-dashboard-header__item ${isOwnChildSelected ||
                          isSubChallengesCompleted(challenges, challenge.index)
                          ? "challenge-dashboard-header__item--active"
                          : ""
                          }`}
                        onClick={() => toggleChallenges(challenge.index)}
                      >
                        <ChallengeCheck
                          checked={isSubChallengesCompleted(
                            challenges,
                            challenge.index
                          )}
                        />
                        <span>Módulo {challenge.index + 1}</span>
                        <span className="material-icons">
                          {isParentOpen ? "expand_less" : "expand_more"}
                        </span>
                      </button>
                      <div
                        className={`challenge-dashboard-header__item__content ${isParentOpen
                          ? "challenge-dashboard-header__item__content--open"
                          : ""
                          }`}
                      >
                        {challenge.subChallengeDTO.map(
                          (subChallenge, subChallengeIdx) => {
                            const isChildActive =
                              currentChallenge[0] === challenge.index &&
                              currentChallenge[1] === subChallengeIdx;
                            return (
                              <button
                                key={subChallenge.title}
                                onClick={() => {
                                  const evaluationInProgress = localStorage.getItem("evaluationInProgress");
                                  setCurrentChallenge([challenge.idChallenge, subChallengeIdx]);
                                  if (evaluationInProgress && evaluationInProgress == "true") {
                                    setShowModal(true);
                                    setMobileOpenChallenges(false)
                                    return;
                                  }
                                  handleSelectCurrentChallenge(
                                    challenge.idChallenge,
                                    subChallengeIdx
                                  )
                                }
                                }
                                className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-child ${isChildActive
                                  ? "challenge-dashboard-sidebar__button-child--active"
                                  : ""
                                  }`}
                              >
                                <img
                                  src={
                                    subChallenge.type === ItemType.Text
                                      ? documentIcon
                                      : videoIcon
                                  }
                                  alt={subChallenge.title + " icon"}
                                  width={32}
                                  height={32}
                                />
                                <span>{subChallenge.title}</span>
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  );
                })}
                <div>
                  <button
                    className={`challenge-dashboard-header__item ${evaluationComplete
                      ? "challenge-dashboard-header__item--active"
                      : ""
                      }`}
                    onClick={() => {
                      setShowEvaluation(true)
                      setMobileOpenChallenges(false)
                    }}
                  >
                    <ChallengeCheck
                      checked={
                        evaluationComplete
                      }
                    />
                    <span>Evaluación</span>
                    <span className="material-icons">
                      {showEvaluation ? "expand_less" : "expand_more"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          {
            !showEvaluation &&
            <><div className="back-button__container">
              <button className="back-button" onClick={handlePreviousRoute}>
                <span className="material-icons md-18">arrow_back</span>
                <p>Volver</p>
              </button>
            </div>

              <div className="challenge-nav__center">
                <header className="challenge-nav">
                  {challenges[currentChallenge[0]]?.subChallengeDTO.map(
                    (subChallenge: any, idx: number) => (
                      <div
                        className="challenge-nav__item"
                        style={
                          {
                            backgroundColor: subChallenge.completed
                              ? "#EE2B7B"
                              : "#EEEEEE",
                            "--main-color": subChallenge.completed
                              ? "white"
                              : "#96A5BF",
                          } as React.CSSProperties
                        }
                        key={idx}
                      >
                        <span className="material-icons md-18">done</span>
                        <span className="challenge-nav__item__number">
                          {idx + 1}
                        </span>
                      </div>
                    )
                  )}
                </header>
              </div>
            </>
          }
        </div>
        <div className="challenge-dashboard-content__container">
          {!showEvaluation && currentSubChallenge && currentSubChallenge.type === "1" && (
            <div className="challenge-dashboard-video">
              <h2>{currentSubChallenge.title}</h2>
              <div className="challenge-dashboard-video__container">
                <iframe
                  width="auto"
                  height="auto"
                  src={currentSubChallenge.url ?? ""}
                  title={currentSubChallenge.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <p>{currentSubChallenge.description}</p>
            </div>
          )}

          {showEvaluation && <EvaluationCredits
            setShowEvaluation={setShowEvaluation}
            evaluationBlocked={evaluationBlocked}
            showEvaluation={showEvaluation}
            setShowModal={setShowModal}
            setResponseModalValue={setResponseModalValue}
            setResponseModalAction={setResponseModalAction}
          />}

          {
            !showEvaluation &&
            <div className="challenge-dashboard-content__controls">

              <button
                onClick={handlePrevChallenge}
                className={`challenge-dashboard-content__controls__before ${currentChallenge[0] === 0 && currentChallenge[1] === 0
                  ? "challenge-dashboard-content__controls__before--disabled"
                  : ""
                  }`}
                disabled={currentChallenge[0] === 0 && currentChallenge[1] === 0}
              >
                <span className="material-icons">chevron_left</span>
                <span>Anterior</span>
              </button>

              <button
                disabled={!isCurrentChallengeCorrect}
                onClick={() =>
                  handleChallengeDone(currentChallenge[0], currentChallenge[1])
                }
                className={`challenge-dashboard-content__controls__done ${isCurrentChallengeComplete
                  ? "challenge-dashboard-content__controls__done--active"
                  : ""
                  } ${!isCurrentChallengeCorrect
                    ? "challenge-dashboard-content__controls__done--disabled"
                    : ""
                  }`}
              >
                {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[
                  currentChallenge[1]
                ].completed ? (
                  <>
                    <span className="material-icons md-18">done</span>
                    <span>Hecho</span>
                  </>
                ) : (
                  <span>Ya lo hice</span>
                )}
              </button>
              <button
                onClick={handleNextChallenge}
                className="challenge-dashboard-content__controls__after"
                disabled={isFinalChallenge(
                  currentChallenge[0],
                  currentChallenge[1]
                )}
              >
                <span>Siguiente</span>
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
          }
        </div>
        {showModal && (
          <ConfirmModal open={showModal} handleClose={() => { setShowModal(false); }} >
            <div className="modal-svg">
              <img src={exitDoor} alt="" />
            </div>
            <div className="container-modal">
              <h1 id="modal-title">¿Quieres salir del curso?</h1>
              <p>Si sales tu progreso se perderá.</p>
              <button onClick={() => {
                setResponseModalValue(true);
                setResponseModalAction("changeAnElementOfAModule");
                setShowModal(false);
              }}>Sí</button>
              <button onClick={() => setShowModal(false)}>No</button>
            </div>
          </ConfirmModal>
        )}
      </main>
    </div>
  );
}

export default CourseCreditsDashboard;
